import { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import SEO from '../components/SEO';
import { ChevronDown, ChevronRight } from 'lucide-react';

const endpoints = [
  { group: 'Authentication', items: [
    { method: 'POST', path: '/api/auth/register', desc: 'Create a new account', body: '{"email":"user@example.com","password":"Pass1234","full_name":"John"}', response: '{"access_token":"eyJ...","refresh_token":"eyJ...","token_type":"bearer"}' },
    { method: 'POST', path: '/api/auth/login', desc: 'Login and get tokens', body: '{"email":"user@example.com","password":"Pass1234"}', response: '{"access_token":"eyJ...","refresh_token":"eyJ...","token_type":"bearer"}' },
    { method: 'POST', path: '/api/auth/refresh', desc: 'Refresh access token', body: '{"refresh_token":"eyJ..."}', response: '{"access_token":"eyJ...","refresh_token":"eyJ..."}' },
  ]},
  { group: 'Domain Lookup', items: [
    { method: 'POST', path: '/api/lookup/', desc: 'Check prices for a list of domains', body: '{"domains":["techcrunch.com","mashable.com"]}', response: '{"id":"uuid","total_queried":2,"total_found":2,"results":[...]}' },
    { method: 'GET', path: '/api/lookup/history', desc: 'Get your past lookups', body: null, response: '{"lookups":[...],"total":10}' },
    { method: 'GET', path: '/api/lookup/{id}', desc: 'Re-query a past lookup', body: null, response: '{"id":"uuid","results":[...]}' },
  ]},
  { group: 'Domains', items: [
    { method: 'GET', path: '/api/domains/stats', desc: 'Database statistics (public)', body: null, response: '{"total_domains":491700,"total_value":96485494,"average_price":196}' },
    { method: 'GET', path: '/api/domains/search', desc: 'Browse/filter domains (Pro+)', body: null, response: '{"results":[...],"total":500,"page":1}' },
    { method: 'GET', path: '/api/domains/{name}', desc: 'Domain detail with seller prices', body: null, response: '{"domain":"techcrunch.com","dr":93,"prices":[...]}' },
  ]},
  { group: 'Price History & Alerts', items: [
    { method: 'GET', path: '/api/prices/history/{domain}', desc: 'Price history + trend (Pro+)', body: null, response: '{"domain":"techcrunch.com","history":[...],"trend":{...}}' },
    { method: 'GET', path: '/api/alerts/', desc: 'List your price alerts', body: null, response: '[{"domain_name":"techcrunch.com","condition":"below","target_price":400}]' },
    { method: 'POST', path: '/api/alerts/', desc: 'Create a price alert', body: '{"domain_name":"techcrunch.com","condition":"below","target_price":400}', response: '{"id":"uuid","domain_name":"techcrunch.com",...}' },
    { method: 'DELETE', path: '/api/alerts/{id}', desc: 'Delete a price alert', body: null, response: '{"message":"Alert deleted"}' },
  ]},
  { group: 'Competitor Analysis', items: [
    { method: 'POST', path: '/api/competitor/analyze', desc: 'Upload Ahrefs CSV to analyze competitor backlinks', body: 'multipart/form-data with CSV file', response: '{"matched_domains":120,"total_estimated_spend":24000,...}' },
  ]},
  { group: 'Sellers', items: [
    { method: 'GET', path: '/api/sellers/', desc: 'List all sellers', body: null, response: '{"sellers":[...],"total":10}' },
    { method: 'GET', path: '/api/seller-scores/', desc: 'Sellers with trust scores', body: null, response: '[{"name":"Adsy","trust_score":8.5,...}]' },
  ]},
  { group: 'Export & Saved Lists', items: [
    { method: 'GET', path: '/api/export/csv/{lookup_id}', desc: 'Export as CSV', body: null, response: 'CSV file download' },
    { method: 'GET', path: '/api/export/json/{lookup_id}', desc: 'Export as JSON (Pro+)', body: null, response: 'JSON file download' },
    { method: 'GET', path: '/api/saved-lists/', desc: 'List saved domain lists', body: null, response: '[{"id":"uuid","name":"My List","domain_count":50}]' },
    { method: 'POST', path: '/api/saved-lists/', desc: 'Save a domain list', body: '{"name":"My List","domains":["site1.com"]}', response: '{"id":"uuid","name":"My List",...}' },
  ]},
];

const methodColors: Record<string, string> = { GET: 'bg-green-500', POST: 'bg-blue-500', DELETE: 'bg-red-500', PATCH: 'bg-yellow-500' };

export default function ApiDocs() {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (g: string) => setOpenGroups(p => ({ ...p, [g]: !p[g] }));

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="LinkDiry API Documentation - Backlink Price API"
        description="Integrate guest post and backlink price data into your workflows with the LinkDiry API. Compare backlink prices, check bulk domains, and build your own backlink price comparison tool."
        canonical="/docs"
      />
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">LinkDiry API</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">Integrate backlink price data and bulk backlink checker functionality into your workflows</p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg font-mono text-sm">
            Base URL: <span className="text-brand-600 font-semibold">{window.location.hostname === 'localhost' ? 'http://localhost:8000' : (import.meta.env.VITE_API_URL || window.location.origin)}</span>
          </div>
        </div>

        <div className="mb-8 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Authentication</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">All endpoints require a Bearer token. Get one by registering or logging in.</p>
          <pre className="bg-gray-900 rounded-lg p-4 text-sm font-mono text-green-400 overflow-x-auto">Authorization: Bearer eyJhbGciOiJIUzI1NiIs...</pre>
        </div>

        <div className="space-y-4">
          {endpoints.map(group => {
            const isOpen = openGroups[group.group] !== false;
            return (
              <div key={group.group} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <button onClick={() => toggleGroup(group.group)} className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{group.group}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{group.items.length} endpoints</span>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {group.items.map((ep, i) => (
                      <div key={i} className="p-4 bg-white dark:bg-gray-800/50">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${methodColors[ep.method] || 'bg-gray-500'}`}>{ep.method}</span>
                          <code className="text-sm font-mono text-gray-900 dark:text-white">{ep.path}</code>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{ep.desc}</p>
                        {ep.body && <><p className="text-xs text-gray-500 mb-1">Request:</p><pre className="bg-gray-900 rounded p-3 text-xs font-mono text-green-400 overflow-x-auto mb-2">{ep.body}</pre></>}
                        <p className="text-xs text-gray-500 mb-1">Response:</p>
                        <pre className="bg-gray-900 rounded p-3 text-xs font-mono text-blue-400 overflow-x-auto">{ep.response}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Rate Limits</h2>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 dark:border-gray-700"><th className="py-2 text-left text-gray-500">Plan</th><th className="py-2 text-left text-gray-500">Lookups/day</th><th className="py-2 text-left text-gray-500">Domains/lookup</th><th className="py-2 text-left text-gray-500">API Access</th></tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr><td className="py-2">Free</td><td>5</td><td>50</td><td>No</td></tr>
              <tr><td className="py-2">Pro ($29/mo)</td><td>100</td><td>500</td><td>No</td></tr>
              <tr><td className="py-2">Enterprise ($99/mo)</td><td>Unlimited</td><td>5,000</td><td>Yes</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
